import * as Common from "@sp-crm/core";
import { TimeEntryId, UserId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "components/rpc/trpc";
import { DangerCompactButton } from "components/ui/danger-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useMemo, useState } from "react";
import { useUsers } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";
import { buildDurationOptions } from "./time-entry-utils";

interface TimeEntryRow {
    id: TimeEntryId;
    userId: UserId;
    durationMinutes: number;
    note?: string | null;
}

interface EditableTimeEntryRowProps {
    entry: TimeEntryRow;
    userName: string;
    options: { value: number; label: string }[];
    onSaved: () => void;
}

const EditableTimeEntryRow: React.FC<EditableTimeEntryRowProps> = ({
    entry,
    userName,
    options,
    onSaved,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editDuration, setEditDuration] = useState(entry.durationMinutes);
    const [editNote, setEditNote] = useState(entry.note ?? "");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleStartEditing = useCallback(() => {
        setEditDuration(entry.durationMinutes);
        setEditNote(entry.note ?? "");
        setIsEditing(true);
    }, [entry.durationMinutes, entry.note]);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
    }, []);

    const handleSave = useCallback(async () => {
        if (!editNote.trim()) return;
        setSaving(true);
        try {
            await trpcClient.timeEntries.update.mutate({
                id: entry.id,
                durationMinutes: editDuration,
                note: editNote.trim(),
            });
            setIsEditing(false);
            onSaved();
            handleEvent("time-entry-edited");
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [entry.id, editDuration, editNote, onSaved]);

    const handleDelete = useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Delete time entry",
            "Are you sure you want to delete this time entry?",
            "Delete",
            "Cancel",
        );
        if (!confirmed) return;
        setDeleting(true);
        try {
            await trpcClient.timeEntries.delete.mutate({ id: entry.id });
            setIsEditing(false);
            onSaved();
        } catch {
            await fancyAlert("Error", "Failed to delete time entry. Please try again.");
        } finally {
            setDeleting(false);
        }
    }, [entry.id, onSaved]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Escape") {
                handleCancel();
            }
        },
        [handleCancel],
    );

    if (isEditing) {
        return (
            <div
                className="space-y-2 rounded border border-gray-200 p-2"
                onKeyDown={handleKeyDown}>
                <Select
                    textSize="sm"
                    value={editDuration}
                    className="w-24"
                    onChange={e => setEditDuration(Number(e.target.value))}>
                    {options.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Select>
                <TextArea
                    textSize="sm"
                    rows={3}
                    value={editNote}
                    onChange={e => setEditNote(e.target.value)}
                />
                <div className="flex gap-2">
                    <SecondaryCompactButton
                        onClick={handleSave}
                        disabled={!editNote.trim() || saving}>
                        {saving ? "Saving..." : "Save"}
                    </SecondaryCompactButton>
                    <SecondaryCompactButton onClick={handleCancel}>
                        Cancel
                    </SecondaryCompactButton>
                    <DangerCompactButton onClick={handleDelete} disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </DangerCompactButton>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex gap-3 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1"
            onClick={handleStartEditing}>
            <div className="text-gray-900">{entry.note}</div>
            <div className="text-gray-400 shrink-0 ml-auto">{userName}</div>
        </div>
    );
};

interface RecentTimeEntriesProps {
    clientId: Common.ClientId;
}

const PAGE_SIZE = 20;

export const RecentTimeEntries: React.FC<RecentTimeEntriesProps> = ({ clientId }) => {
    const trpcHooks = trpc.useTRPC();
    const queryClient = useQueryClient();
    const userState = useUsers();
    const { settings } = useTenantSettings();
    const increment = settings.timeTrackingIncrementMinutes || 15;
    const options = useMemo(() => buildDurationOptions(increment), [increment]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const { data: entries, isLoading } = useQuery(
        trpcHooks.timeEntries.listByClient.queryOptions({
            clientId,
            limit: visibleCount + 1,
        }),
    );

    const handleSaved = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
    }, [queryClient]);

    const handleLoadMore = useCallback(() => {
        setVisibleCount(prev => prev + PAGE_SIZE);
    }, []);

    if (isLoading) {
        return <div className="text-sm text-gray-500">Loading entries...</div>;
    }

    if (!entries || entries.length === 0) {
        return <div className="text-sm text-gray-500">No time entries yet.</div>;
    }

    const hasMore = entries.length > visibleCount;
    const visibleEntries = hasMore ? entries.slice(0, visibleCount) : entries;

    return (
        <div className="space-y-1">
            {visibleEntries.map(entry => (
                <EditableTimeEntryRow
                    key={entry.id}
                    entry={entry}
                    userName={userState.users[entry.userId]?.name ?? ""}
                    options={options}
                    onSaved={handleSaved}
                />
            ))}
            {hasMore && (
                <SecondaryCompactButton onClick={handleLoadMore}>
                    Load more
                </SecondaryCompactButton>
            )}
        </div>
    );
};
